import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { epochLabel } from '@/features/projects/format'
import { selectProjectMembers } from '@/features/projects/slices/projectDetailSlice'
import { useAppDispatch, useAppSelector } from '@/store'
import { MemberSelect } from './MemberSelect'
import {
  createMigrationPlan,
  openMigration,
  planSelected,
  selectCreatePlanError,
  selectCreatingPlan,
  selectMigrationBlocked,
  selectMigrationError,
  selectMigrationLoading,
  selectMigrationNonce,
  selectMigrationPlans,
  selectSelectedPlanId,
  toDisplayError
} from '../slices/migrationSlice'
import { ROLLBACK_DESCRIPTION_MIN_LENGTH, type MigrationPlanStatus } from '../types'

const PLAN_TONE: Record<MigrationPlanStatus, StatusTone> = {
  DRAFT: 'neutral',
  READY: 'proposed',
  CUTOVER_SCHEDULED: 'decided'
}

/** In-scope count derived from the plan's own scope rows (AC-1) — `inScopeCount()` is `@JsonIgnore` server-side. */
const inScopeCount = (plan: { scope: readonly { inScope: boolean }[] }): number =>
  plan.scope.filter((entry) => entry.inScope).length

/**
 * The plans panel (AC-1, AC-2) — self-contained: owns its own fetch, its
 * own loading/error/empty triad, and the create-plan action on an empty
 * project. Renders exactly `CreateMigrationPlanRequest`'s declared fields
 * (`ownerId`, `rollbackPath.ownerId`, `rollbackPath.description`,
 * `linkedTaskLegacyId?`) — no other plan-authoring write is offered here
 * (Decision 1: the scope/cutover-window/owner/status forms are a separate
 * deliverable).
 */
export function MigrationPlansPanel({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const plans = useAppSelector(selectMigrationPlans)
  const selectedPlanId = useAppSelector(selectSelectedPlanId)
  const loading = useAppSelector(selectMigrationLoading)
  const error = useAppSelector(selectMigrationError)
  const blocked = useAppSelector(selectMigrationBlocked)
  const nonce = useAppSelector(selectMigrationNonce)
  const creating = useAppSelector(selectCreatingPlan)
  const createError = useAppSelector(selectCreatePlanError)
  const members = useAppSelector(selectProjectMembers)

  const [showForm, setShowForm] = useState(false)
  const [ownerId, setOwnerId] = useState('')
  const [rollbackOwnerId, setRollbackOwnerId] = useState('')
  const [rollbackDescription, setRollbackDescription] = useState('')
  const [linkedTaskLegacyId, setLinkedTaskLegacyId] = useState('')

  // Finding 4 (AC-3): submitting empty relied on invisible native `required`
  // bubbles. Create is now disabled — with a visible reason — until both
  // owners are chosen and the description clears the server's own minimum.
  const complete =
    ownerId !== '' &&
    rollbackOwnerId !== '' &&
    rollbackDescription.trim().length >= ROLLBACK_DESCRIPTION_MIN_LENGTH

  useEffect(() => {
    void dispatch(openMigration({ orgId, projectId }))
  }, [dispatch, orgId, projectId, nonce])

  const onRetryClicked = useCallback(() => {
    void dispatch(openMigration({ orgId, projectId }))
  }, [dispatch, orgId, projectId])

  const onPlanRowClicked = useCallback(
    (planId: string) => {
      dispatch(planSelected(planId))
    },
    [dispatch]
  )

  const onCreatePlanClicked = useCallback(() => {
    setShowForm(true)
  }, [])

  const onSubmitCreatePlan = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      // Defence for an Enter-key submit bypassing the disabled button.
      if (!complete) return
      void dispatch(
        createMigrationPlan({
          orgId,
          projectId,
          input: {
            ownerId,
            rollbackPath: { ownerId: rollbackOwnerId, description: rollbackDescription },
            linkedTaskLegacyId: linkedTaskLegacyId.trim() === '' ? undefined : linkedTaskLegacyId
          }
        })
      ).then((result) => {
        if (createMigrationPlan.fulfilled.match(result)) {
          setShowForm(false)
          setOwnerId('')
          setRollbackOwnerId('')
          setRollbackDescription('')
          setLinkedTaskLegacyId('')
        }
      })
    },
    [dispatch, orgId, projectId, ownerId, rollbackOwnerId, rollbackDescription, linkedTaskLegacyId, complete]
  )

  // The page-level blocked state is rendered one level up (INV-5.3); this
  // panel still returns nothing while blocked so it never shows stale rows.
  if (blocked !== null) return null

  return (
    <section
      aria-label={t('migration.plans.title')}
      className="rounded-lg border border-border bg-card px-4 py-3.5"
      data-testid="migration-plans-panel"
    >
      <SectionLabel>{t('migration.plans.title')}</SectionLabel>

      {error !== null ? (
        <div className="mt-2">
          <ErrorPanel error={toDisplayError(error)} onRetryClicked={onRetryClicked} />
        </div>
      ) : loading && plans.length === 0 ? (
        <div className="mt-2">
          <LoadingPanel label={t('migration.plans.loading')} />
        </div>
      ) : plans.length === 0 ? (
        <div className="mt-2">
          <EmptyPanel
            title={t('migration.plans.emptyTitle')}
            body={t('migration.plans.emptyBody')}
            actionLabel={showForm ? undefined : t('migration.plans.createAction')}
            onActionClicked={showForm ? undefined : onCreatePlanClicked}
          />
          {showForm && (
            <form onSubmit={onSubmitCreatePlan} className="mt-3 space-y-3 rounded-lg border border-border bg-muted/30 p-4">
              <div>
                <label htmlFor="migration-owner-id" className="text-xs font-medium text-foreground">
                  {t('migration.plans.form.ownerId')}
                </label>
                <MemberSelect
                  id="migration-owner-id"
                  members={members}
                  value={ownerId}
                  onChange={setOwnerId}
                  required
                  className="mt-1 w-full"
                />
              </div>
              <div>
                <label htmlFor="migration-rollback-owner-id" className="text-xs font-medium text-foreground">
                  {t('migration.plans.form.rollbackOwnerId')}
                </label>
                <MemberSelect
                  id="migration-rollback-owner-id"
                  members={members}
                  value={rollbackOwnerId}
                  onChange={setRollbackOwnerId}
                  required
                  className="mt-1 w-full"
                />
              </div>
              <div>
                <label htmlFor="migration-rollback-description" className="text-xs font-medium text-foreground">
                  {t('migration.plans.form.rollbackDescription')}
                </label>
                <Input
                  id="migration-rollback-description"
                  value={rollbackDescription}
                  onChange={(e) => setRollbackDescription(e.target.value)}
                  required
                  minLength={ROLLBACK_DESCRIPTION_MIN_LENGTH}
                  className="mt-1"
                />
              </div>
              <div>
                <label htmlFor="migration-linked-task" className="text-xs font-medium text-foreground">
                  {t('migration.plans.form.linkedTask')}
                </label>
                <Input
                  id="migration-linked-task"
                  value={linkedTaskLegacyId}
                  onChange={(e) => setLinkedTaskLegacyId(e.target.value)}
                  className="mt-1"
                />
              </div>
              {createError !== null && (
                <p role="alert" className="text-xs text-blocked">
                  {createError.message}
                </p>
              )}
              {!complete && (
                <p data-testid="migration-create-incomplete" className="text-xs text-muted-foreground">
                  {t('migration.plans.form.incompleteHint')}
                </p>
              )}
              <div className="flex gap-2">
                <Button type="submit" disabled={creating || !complete}>
                  {t('migration.plans.form.submit')}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  {t('migration.plans.form.cancel')}
                </Button>
              </div>
            </form>
          )}
        </div>
      ) : (
        <ul className="mt-2 overflow-hidden rounded-lg border border-border">
          {plans.map((plan) => (
            <li key={plan.id}>
              <button
                type="button"
                onClick={() => onPlanRowClicked(plan.id)}
                aria-pressed={plan.id === selectedPlanId}
                data-testid={`migration-plan-row-${plan.id}`}
                className={`v-row flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left ${
                  plan.id === selectedPlanId ? 'bg-accent' : 'hover:bg-muted'
                }`}
              >
                <StatusPill tone={PLAN_TONE[plan.status]}>{t(`migration.planStatus.${plan.status}`)}</StatusPill>
                <span className="text-sm font-medium text-foreground">{plan.ownerName}</span>
                <span className="text-xs text-muted-foreground">
                  {t('migration.plans.inScope', { count: inScopeCount(plan) })}
                </span>
                <span className="text-xs text-muted-foreground">
                  {plan.rollbackPath.tested
                    ? t('migration.plans.rollbackTested')
                    : t('migration.plans.rollbackNotTested')}
                </span>
                {plan.cutoverWindow?.plannedStart !== undefined && (
                  <span className="text-xs text-muted-foreground">
                    {t('migration.plans.cutover', {
                      start: plan.cutoverWindow.plannedStart,
                      end: plan.cutoverWindow.plannedEnd ?? '—'
                    })}
                  </span>
                )}
                <span className="ml-auto text-xs text-muted-foreground">{epochLabel(plan.createdAt)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
